import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import { PAPER_SIZES, FONT_SCALE } from "../../constants/flyer";
import { readableTextColor } from "./imaging";

// Flyer record and PDF for /start. The record is created first without a PDF
// (the backend allows it, and it is what marks the setup step done), then the
// PDF is rendered on the phone and attached with an update. The update replaces
// every attribute, so it always sends the complete record.

const PX_PER_CM = 37.8;

export function flyerDescriptionHtml(t) {
  return `<p style="text-align: center">${t("flyers.preview.scanInfo.title")}<br />
${t("flyers.preview.scanInfo.description")}<br />
<strong>${t("flyers.preview.scanInfo.apple")}</strong> & <strong>${t("flyers.preview.scanInfo.google")}</strong></p>`;
}

export function buildFlyerRecord({ formId, paperSize, backgroundColor, textColor, t }) {
  return {
    form_id: formId,
    paper_size: PAPER_SIZES[paperSize] ? paperSize : "a4",
    header_text: t("flyers.new.form.defaultHeaderText"),
    background_color: backgroundColor,
    text_color: textColor,
    description: flyerDescriptionHtml(t),
  };
}

export function paperPixels(paperSize) {
  const paper = PAPER_SIZES[paperSize] || PAPER_SIZES.a4;
  return { width: paper.width * PX_PER_CM, height: paper.height * PX_PER_CM, paper };
}

function withTimeout(promise, ms, message) {
  let timer;
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

// Renders a mounted FlyerRender element (sized to the paper in px, off screen)
// to a JPEG canvas and wraps it in a one-page PDF, the way the flyer editor
// does. Returns { pdf: Blob, jpeg: Blob }.
export async function renderFlyerFiles(element, paperSize, backgroundColor) {
  if (!element) throw new Error("Flyer element not mounted");
  const { paper } = paperPixels(paperSize);
  try {
    await withTimeout(document.fonts?.ready ?? Promise.resolve(), 3000, "fonts");
  } catch {
    // render with whatever font is ready
  }
  const canvas = await withTimeout(
    html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      imageTimeout: 15000,
      logging: false,
      backgroundColor: backgroundColor || null,
    }),
    30000,
    "Flyer render timed out",
  );
  const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const pdf = new jsPDF({ orientation: "portrait", unit: "cm", format: [paper.width, paper.height] });
  pdf.addImage(jpegDataUrl, "JPEG", 0, 0, paper.width, paper.height);
  const pdfBlob = pdf.output("blob");
  const jpegBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  canvas.width = 0;
  canvas.height = 0;
  return { pdf: pdfBlob, jpeg: jpegBlob };
}

export function flyerFontSizePx(paperSize) {
  return paperPixels(paperSize).width * FONT_SCALE;
}

// The flyer uses the store's colours, like the dashboard's flyer editor.
export function flyerColors(store) {
  const backgroundColor = store?.background_color || "#FFFFFF";
  return { backgroundColor, textColor: store?.primary_color || readableTextColor(backgroundColor) };
}
